import { useEffect, useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Icon } from '@/components/ui/icon';
import { Label } from '@/components/ui/label';

const DISMISS_KEY = 'install-prompt-dismissed';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

// Registrado no carregamento do módulo: o Chrome pode disparar o evento antes do React montar.
let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    for (const notify of listeners) notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    localStorage.setItem(DISMISS_KEY, '1');
  });
}

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIos() {
  const ua = navigator.userAgent;
  // iPadOS se apresenta como Mac; o toque denuncia.
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    (ua.includes('Macintosh') && navigator.maxTouchPoints > 1)
  );
}

function isMobile() {
  return isIos() || /Android/i.test(navigator.userAgent);
}

export function InstallPrompt() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'native' | 'ios'>('native');
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const checkboxId = useId();

  useEffect(() => {
    if (!isMobile() || isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    if (isIos()) {
      // Safari não tem prompt de instalação: mostramos as instruções manualmente.
      setMode('ios');
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }

    const show = () => {
      setMode('native');
      setOpen(true);
    };
    if (deferredPrompt) show();
    listeners.add(show);
    return () => {
      listeners.delete(show);
    };
  }, []);

  function close() {
    if (dontShowAgain) localStorage.setItem(DISMISS_KEY, '1');
    setOpen(false);
  }

  async function install() {
    if (!deferredPrompt) return close();
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    if (outcome === 'accepted') localStorage.setItem(DISMISS_KEY, '1');
    close();
  }

  return (
    <Drawer open={open} onOpenChange={(value) => (value ? setOpen(true) : close())}>
      <DrawerContent>
        <DrawerHeader className="items-center gap-3">
          <img src="/assets/icon-192.png" alt="" className="size-14 rounded-[14px]" />
          <DrawerTitle className="font-display text-lg font-extrabold uppercase">
            Instalar o Chordfy
          </DrawerTitle>
          <DrawerDescription>
            {mode === 'ios'
              ? 'Adicione o admin à tela de início para abrir como um app, em tela cheia.'
              : 'Instale o admin no celular para abrir como um app, direto da tela de início.'}
          </DrawerDescription>
        </DrawerHeader>

        {mode === 'ios' && (
          <ol className="text-foreground-2 mx-4 flex flex-col gap-2 rounded-[12px] border border-border bg-card p-4">
            <li className="flex items-center gap-2">
              <span className="text-muted-foreground">1.</span> Toque em
              <Icon name="share" className="text-olive-icon size-4" />
              <strong className="text-foreground">Compartilhar</strong>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-muted-foreground">2.</span> Escolha
              <Icon name="square-plus" className="text-olive-icon size-4" />
              <strong className="text-foreground">Adicionar à Tela de Início</strong>
            </li>
          </ol>
        )}

        <DrawerFooter>
          <div className="flex items-center justify-center gap-2 pb-2">
            <Checkbox
              id={checkboxId}
              checked={dontShowAgain}
              onCheckedChange={(value) => setDontShowAgain(value === true)}
            />
            <Label htmlFor={checkboxId} className="text-muted-foreground font-normal">
              Não mostrar novamente
            </Label>
          </div>
          {mode === 'native' && (
            <Button size="lg" icon="download" onClick={install}>
              Instalar
            </Button>
          )}
          <Button size="lg" variant="secondary" onClick={close}>
            {mode === 'ios' ? 'Entendi' : 'Agora não'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
